import React, { useState, useRef, useCallback, useEffect } from 'react';
import videoService from '../services/videoService';
import uploadService from '../services/uploadService';
import indexedDBService from '../services/indexedDBService';
import { extractErrorMessage } from '../services/api';
import { useUpload } from '../contexts/UploadContext';
import './Recording.css';

const Recording = () => {
    const videoRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const chunksRef = useRef([]);
    const inputRef = useRef(null);

    const { addUpload, updateUploadProgress, completeUpload } = useUpload();

    const [awb, setAwb] = useState('');
    const [scanType, setScanType] = useState('forward');
    const [activeAwb, setActiveAwb] = useState('');
    const [activeScanType, setActiveScanType] = useState('');
    const [recordingMode, setRecordingMode] = useState('manual');
    const [countdown, setCountdown] = useState(null);
    const [isRecording, setIsRecording] = useState(false);
    const [recordedBlob, setRecordedBlob] = useState(null);
    const [recordedUrl, setRecordedUrl] = useState(null);
    const [cameraError, setCameraError] = useState(null);
    const [cameraReady, setCameraReady] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [recordingTime, setRecordingTime] = useState(0);
    const [message, setMessage] = useState({ type: '', text: '' });

    const pendingDataRef = useRef(null);
    const timerRef = useRef(null);
    const autoStartTimerRef = useRef(null);

    // AWB validation
    const awbRegex = /^[A-Za-z0-9]{8,20}$/;
    const isAwbValid = awbRegex.test(awb);



    const startCamera = useCallback(async (retryWithVideoOnly = true) => {
        try {
            setCameraError(null);
            const constraints = {
                video: {
                    width: { ideal: 1280 },
                    height: { ideal: 720 },
                    facingMode: 'environment'
                },
                audio: true
            };

            // Attempt to get stream with audio
            const stream = await navigator.mediaDevices.getUserMedia(constraints);

            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                setCameraReady(true);
            }
        } catch (error) {
            console.error('Camera error:', error);

            // If audio fails and we haven't tried video-only yet, retry
            if (retryWithVideoOnly && (error.name === 'NotAllowedError' || error.name === 'NotFoundError' || error.name === 'PermissionDeniedError')) {
                try {
                    console.log('Retrying with video only...');
                    const videoOnlyStream = await navigator.mediaDevices.getUserMedia({
                        video: {
                            width: { ideal: 1280 },
                            height: { ideal: 720 },
                            facingMode: 'environment'
                        },
                        audio: false
                    });

                    if (videoRef.current) {
                        videoRef.current.srcObject = videoOnlyStream;
                        setCameraReady(true);
                        return;
                    }
                } catch (videoError) {
                    console.error('Video only fallback failed:', videoError);
                    error = videoError; // Use the video error for the final message
                }
            }

            let errorMessage = 'Could not access camera. Please check your device.';

            if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
                errorMessage = 'Camera access denied. Please allow camera permission.';
            } else if (!window.isSecureContext) {
                errorMessage = 'Camera access requires a secure connection (HTTPS). Please contact administrator.';
            }

            setCameraError(errorMessage);
        }
    }, []);

    const stopCamera = useCallback(() => {
        if (videoRef.current?.srcObject) {
            videoRef.current.srcObject.getTracks().forEach(track => track.stop());
        }
    }, []);

    const processBackgroundUpload = useCallback(async (blob, data, { silent = true } = {}) => {
        const { awb: uploadAwb, scanType: uploadScanType } = data;
        let uploadId = null;

        try {
            const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
            const filename = `${uploadAwb}_${uploadScanType}_${timestamp}.mp4`;

            // Get signed upload URL
            const urlResult = await videoService.getUploadUrl(
                uploadAwb,
                uploadScanType,
                filename,
                blob.size
            );

            if (!urlResult.success) {
                throw new Error('Failed to get upload URL');
            }

            uploadId = urlResult.uploadId;
            const { signedUrl } = urlResult;

            // Persist blob to IndexedDB before attempting S3 upload
            // so it can be recovered if the browser closes mid-upload
            await indexedDBService.saveBlob(uploadId, blob, {
                awb: uploadAwb,
                scanType: uploadScanType,
                filename,
                fileSize: blob.size
            });

            // Add to active uploads
            addUpload({
                uploadId,
                awb: uploadAwb,
                scanType: uploadScanType,
                filename,
                progress: 0,
                status: 'uploading'
            });

            // Upload to S3
            await uploadService.uploadToS3(signedUrl, blob, (progress) => {
                updateUploadProgress(uploadId, progress);
            });

            // Mark upload as complete and remove from IndexedDB
            await completeUpload(uploadId);
            await indexedDBService.deleteBlob(uploadId);

            console.log(`Upload successful for AWB: ${uploadAwb}`);
        } catch (error) {
            console.error('Background upload error:', error);
            // Blob stays in IndexedDB for recovery — operator will see it on next login.
            // The "Next" auto-flow fires this in the background and can't surface an
            // error to anyone, so it stays silent by default. The Submit button flow
            // passes { silent: false } so the operator sees the real failure instead
            // of a false "uploaded successfully" message.
            if (!silent) throw error;
        }
    }, [addUpload, completeUpload, updateUploadProgress]);

    const startRecording = useCallback(() => {
        if (!isAwbValid) {
            setMessage({ type: 'error', text: 'Please enter a valid AWB (8-20 alphanumeric characters)' });
            return;
        }

        setIsRecording(true);
        setActiveAwb(awb);
        setActiveScanType(scanType);
        setAwb(''); // Clear input for next scan

        chunksRef.current = [];
        setRecordedBlob(null);
        setRecordedUrl(null);
        setMessage({ type: '', text: '' });

        const stream = videoRef.current.srcObject;
        const hasAudio = stream.getAudioTracks().length > 0;

        let options = { mimeType: 'video/webm;codecs=vp9' };

        // Try to include audio codec only if audio is available
        if (hasAudio) {
            if (MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus')) {
                options.mimeType = 'video/webm;codecs=vp9,opus';
            } else if (MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus')) {
                options.mimeType = 'video/webm;codecs=vp8,opus';
            }
        } else {
            // Video only options
            if (!MediaRecorder.isTypeSupported(options.mimeType)) {
                options.mimeType = 'video/webm';
            }
        }

        const mediaRecorder = new MediaRecorder(stream, options);

        mediaRecorder.ondataavailable = (e) => {
            if (e.data.size > 0) {
                chunksRef.current.push(e.data);
            }
        };

        mediaRecorder.onstop = () => {
            const blob = new Blob(chunksRef.current, { type: 'video/webm' });

            if (pendingDataRef.current) {
                // Background upload for "Next" flow
                processBackgroundUpload(blob, pendingDataRef.current);
                pendingDataRef.current = null;
            } else {
                // Normal "Stop" flow - show preview
                setRecordedBlob(blob);
                setRecordedUrl(URL.createObjectURL(blob));
            }
        };

        mediaRecorderRef.current = mediaRecorder;
        mediaRecorder.start(1000); // Collect data every second
        setRecordingTime(0);

        // Start timer
        timerRef.current = setInterval(() => {
            setRecordingTime(prev => prev + 1);
        }, 1000);
    }, [awb, scanType, isAwbValid, processBackgroundUpload]);



    const stopRecording = useCallback(() => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        }
    }, [isRecording]);


    const handleNext = useCallback((nextAwb = '', nextScanType = 'forward') => {
        if (!activeAwb) return;

        // Capture current active data for the background upload
        const currentData = { awb: activeAwb, scanType: activeScanType };
        pendingDataRef.current = currentData;

        // Stop current recording
        stopRecording();

        // If a next AWB was provided (Auto-flow), start new recording immediately
        if (nextAwb) {
            // Need a slight delay to ensure state updates and previous tracks are handled if necessary
            // or just trigger it immediately if startRecording handles the chunksRef reset
            setTimeout(() => {
                setAwb(nextAwb);
                setScanType(nextScanType);
                // We'll call startRecording in the onKeyDown or here if logic allows
                // Better to handle it in onKeyDown to keep startRecording pure
            }, 0);
        }

        setMessage({ type: 'success', text: `Previous upload (${activeAwb}) started in background.` });
        setActiveAwb('');
        setActiveScanType('');

        // Return focus to input for next scan
        if (inputRef.current) inputRef.current.focus();

        // Clear message after 3 seconds
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    }, [activeAwb, activeScanType, stopRecording]);

    // Initialize camera
    useEffect(() => {
        startCamera();
        // Focus AWB input on mount if camera is ready or error handled
        if (inputRef.current) inputRef.current.focus();

        return () => {
            stopCamera();
            if (timerRef.current) clearInterval(timerRef.current);
            if (autoStartTimerRef.current) clearInterval(autoStartTimerRef.current);
        };
    }, [startCamera]); // Added dependency to be safer, though startCamera is not yet a stable callback. Actually startCamera is a plain function right now.

    // Auto-start/next logic
    useEffect(() => {
        if (recordingMode === 'auto' && isAwbValid && !uploading && cameraReady) {
            // Start 3s countdown
            setCountdown(3);
            if (autoStartTimerRef.current) clearInterval(autoStartTimerRef.current);

            let timeLeft = 3;
            autoStartTimerRef.current = setInterval(() => {
                timeLeft -= 1;
                if (timeLeft <= 0) {
                    clearInterval(autoStartTimerRef.current);
                    setCountdown(null);

                    if (isRecording) {
                        // AUTO NEXT: Stop current (background upload) and start next
                        const nextAwb = awb;
                        const nextScanType = scanType;

                        pendingDataRef.current = { awb: activeAwb, scanType: activeScanType };
                        stopRecording();

                        // Short timeout to allow state to settle
                        setTimeout(() => {
                            setAwb(nextAwb);
                            setScanType(nextScanType);
                            document.getElementById('hidden-start-trigger')?.click();
                        }, 50);
                    } else if (!recordedBlob) {
                        // AUTO START
                        startRecording();
                    }
                } else {
                    setCountdown(timeLeft);
                }
            }, 1000);
        } else {
            // Clear countdown if conditions not met
            setCountdown(null);
            if (autoStartTimerRef.current) clearInterval(autoStartTimerRef.current);
        }

        return () => {
            if (autoStartTimerRef.current) clearInterval(autoStartTimerRef.current);
        };
    }, [awb, recordingMode, isAwbValid, isRecording, recordedBlob, uploading, cameraReady, activeAwb, activeScanType, stopRecording, startRecording]);

    const handleSubmit = async () => {
        // The AWB for the recorded clip lives in `activeAwb` — it was captured when
        // recording started and `awb` was cleared right after for the *next* scan.
        // Validating against `awb` here meant this always no-op'd once the input
        // was empty (i.e. every time, right after a normal Stop).
        if (!recordedBlob || !activeAwb) return;

        try {
            setUploading(true);
            setUploadProgress(0);
            setMessage({ type: '', text: '' });

            await processBackgroundUpload(
                recordedBlob,
                { awb: activeAwb, scanType: activeScanType },
                { silent: false }
            );

            setMessage({ type: 'success', text: 'Video uploaded successfully!' });

            // Reset form (only on confirmed success)
            setRecordedBlob(null);
            setRecordedUrl(null);
            setAwb('');
            setActiveAwb('');
            setActiveScanType('');
            setUploadProgress(0);
            if (inputRef.current) inputRef.current.focus();
        } catch (error) {
            console.error('Upload error:', error);
            // Keep the recorded video on screen so the operator can retry Submit —
            // it's still safely persisted in IndexedDB either way.
            setMessage({ type: 'error', text: extractErrorMessage(error, 'Upload failed. Please try again.') });
        } finally {
            setUploading(false);
        }
    };

    const discardRecording = () => {
        setRecordedBlob(null);
        setRecordedUrl(null);
        setMessage({ type: '', text: '' });
        if (inputRef.current) inputRef.current.focus();
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    return (
        <div className="recording-page">
            <div className="recording-container">
                {/* Video Preview */}
                <div className="video-section">
                    <div className="video-wrapper">
                        {cameraError ? (
                            <div className="camera-error">
                                <i className="fas fa-camera-slash" style={{ fontSize: '48px', opacity: 0.2, marginBottom: '16px' }}></i>
                                <p>{cameraError}</p>
                                <button className="btn btn-primary" style={{ marginTop: '16px' }} onClick={startCamera}>
                                    Retry
                                </button>
                            </div>
                        ) : recordedUrl && !isRecording ? (
                            <video
                                src={recordedUrl}
                                controls
                                className="recorded-video"
                            />
                        ) : (
                            <video
                                ref={videoRef}
                                autoPlay
                                muted
                                playsInline
                                className="live-video"
                            />
                        )}

                        {isRecording && (
                            <div className="recording-indicator">
                                <span className="rec-dot"></span>
                                <span>REC</span>
                                <span className="rec-time">{formatTime(recordingTime)}</span>
                                {activeAwb && <span className="active-awb-label">AWB: {activeAwb}</span>}
                            </div>
                        )}
                    </div>
                </div>

                {/* Controls */}
                <div className="controls-section">
                    <div className="control-group">
                        <label>Recording Mode</label>
                        <div className="mode-selector">
                            <button
                                className={`mode-btn ${recordingMode === 'manual' ? 'active' : ''}`}
                                onClick={() => setRecordingMode('manual')}
                            >
                                <i className="fas fa-hand-pointer"></i> Manual
                            </button>
                            <button
                                className={`mode-btn ${recordingMode === 'auto' ? 'active' : ''}`}
                                onClick={() => setRecordingMode('auto')}
                            >
                                <i className="fas fa-magic"></i> Automatic
                            </button>
                        </div>
                    </div>

                    <div className="control-group">
                        <label>AWB Number</label>
                        <input
                            ref={inputRef}
                            type="text"
                            value={awb}
                            onChange={(e) => setAwb(e.target.value.toUpperCase())}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    if (isAwbValid) {
                                        if (isRecording) {
                                            // AUTO NEXT: Stop current and start next
                                            const nextAwb = awb;
                                            const nextScanType = scanType;

                                            // Capture current active data for background
                                            pendingDataRef.current = { awb: activeAwb, scanType: activeScanType };
                                            stopRecording();

                                            // Short timeout to allow state to settle
                                            setTimeout(() => {
                                                setAwb(nextAwb);
                                                setScanType(nextScanType);
                                                // We can't call startRecording directly here because of closure on 'awb'
                                                // We'll use a trick or just trigger it manually
                                                document.getElementById('hidden-start-trigger')?.click();
                                            }, 50);
                                        } else if (!recordedBlob && !uploading && cameraReady) {
                                            // AUTO START
                                            startRecording();
                                        }
                                    }
                                }
                            }}
                            placeholder="Enter or scan AWB"
                            disabled={uploading}
                            className={awb && !isAwbValid ? 'invalid' : ''}
                        />
                        {/* Hidden trigger for auto-flow start */}
                        <button
                            id="hidden-start-trigger"
                            style={{ display: 'none' }}
                            onClick={startRecording}
                        ></button>
                        {awb && !isAwbValid && (
                            <span className="validation-error">8-20 alphanumeric characters required</span>
                        )}
                    </div>

                    <div className="control-group">
                        <label>Scan Type</label>
                        <div className="scan-type-buttons">
                            <button
                                className={`scan-btn ${scanType === 'forward' ? 'active' : ''}`}
                                onClick={() => setScanType('forward')}
                                disabled={isRecording || uploading}
                            >
                                <i className="fas fa-box" style={{ marginRight: '8px' }}></i> Forward
                            </button>
                            <button
                                className={`scan-btn ${scanType === 'backward' ? 'active' : ''}`}
                                onClick={() => setScanType('backward')}
                                disabled={isRecording || uploading}
                            >
                                <i className="fas fa-undo" style={{ marginRight: '8px' }}></i> Backward
                            </button>
                        </div>
                    </div>

                    {/* Message Display */}
                    {countdown !== null && (
                        <div className="message info countdown">
                            <i className="fas fa-stopwatch animate-pulse"></i>
                            Auto-starting in {countdown}s...
                        </div>
                    )}

                    {message.text && (
                        <div className={`message ${message.type}`}>
                            <i className={`fas ${message.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-triangle'}`} style={{ marginRight: '8px' }}></i>
                            {message.text}
                        </div>
                    )}

                    {/* Upload Progress */}
                    {uploading && (
                        <div className="upload-progress-section">
                            <div className="progress-label">
                                Uploading... {uploadProgress}%
                            </div>
                            <div className="progress-track">
                                <div
                                    className="progress-fill"
                                    style={{ width: `${uploadProgress}%` }}
                                ></div>
                            </div>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="action-buttons">
                        {!recordedBlob && !isRecording && (
                            <button
                                className="btn btn-record"
                                onClick={startRecording}
                                disabled={!cameraReady || !isAwbValid || uploading}
                            >
                                <i className="fas fa-circle" style={{ marginRight: '8px', fontSize: '10px' }}></i> Start Recording
                            </button>
                        )}

                        {isRecording && (
                            <div style={{ display: 'flex', gap: '12px' }}>
                                <button
                                    className="btn btn-stop"
                                    style={{ flex: 1 }}
                                    onClick={stopRecording}
                                >
                                    <i className="fas fa-stop" style={{ marginRight: '8px' }}></i> Stop
                                </button>
                                <button
                                    className="btn btn-next"
                                    style={{ flex: 1.5 }}
                                    onClick={() => handleNext()}
                                >
                                    Next <i className="fas fa-arrow-right" style={{ marginLeft: '8px' }}></i>
                                </button>
                            </div>
                        )}

                        {recordedBlob && !isRecording && !uploading && (
                            <>
                                <button
                                    className="btn btn-primary"
                                    onClick={handleSubmit}
                                >
                                    <i className="fas fa-upload" style={{ marginRight: '8px' }}></i> Submit
                                </button>
                                <div style={{ display: 'flex', gap: '12px' }}>
                                    <button
                                        className="btn btn-secondary"
                                        style={{ flex: 1 }}
                                        onClick={discardRecording}
                                    >
                                        <i className="fas fa-trash" style={{ marginRight: '8px' }}></i> Discard
                                    </button>
                                    <button
                                        className="btn btn-secondary"
                                        style={{ flex: 1 }}
                                        onClick={startRecording}
                                    >
                                        <i className="fas fa-redo" style={{ marginRight: '8px' }}></i> Re-record
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Recording;
